import React from 'react';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  gameDateToMatchTime,
  gameDateToMatchDay,
  MarketIdToMarketName,
  getSportTypeToIconForBetSlip,
  MyBetsTextCreator,
  sportTypeToSportId
} from 'toolkit/utils';
import { AWS_BUCKET_URL } from 'toolkit/constants';
import { getTeamNameHelper } from 'helper/matchDetailHelper';
import { EnumMarketName, EnumMatchStatusName } from 'toolkit/Enums.js';
import { useMediaQuery } from 'react-responsive';
import Jersey from 'components/jersey';
import { selectEventById as selectOutrightEventById } from 'stores/outrights';

function BetMatchCard(props) {
  const navigate = useNavigate();
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const liveEvents = useSelector((state) => {
    const { liveMatches, events } = state.match;
    return liveMatches.map((c) => events[c]);
  }, shallowEqual);
  const match = props.match;
  const locales = useSelector((state) => state.match.locales);
  const { t } = useTranslation('translations', { useSuspense: false });
  const teams =
    match.home === null || match.away === null
      ? ['Home', 'Away']
      : [
        getTeamNameHelper(locales, selectedLocale, match.home, t),
        getTeamNameHelper(locales, selectedLocale, match.away, t)
      ];
  const sportType = match.sportType;
  let homeScore1,
    homeScore2,
    awayScore1,
    awayScore2,
    homeScore3,
    awayScore3 = 0;
  let firstHalfHomeScore, firstHalfAwayScore;
  const isDesktop = useMediaQuery({
    query: '(min-width: 992px)'
  });

  if (match.periodScores) {
    homeScore1 = match.periodScores[0]?.homeScore;
    homeScore2 = match.periodScores[1]?.homeScore;
    homeScore3 = match.periodScores[2]?.homeScore;
    awayScore1 = match.periodScores[0]?.awayScore;
    awayScore2 = match.periodScores[1]?.awayScore;
    awayScore3 = match.periodScores[2]?.awayScore;
  }
  if (homeScore1 && homeScore2) {
    firstHalfHomeScore = homeScore1 + homeScore2;
    firstHalfAwayScore = awayScore1 + awayScore2;
  }

  function getTime(props) {
    let timeLabel = '';
    if (match) {
      const { matchStatus } = props;
      switch (matchStatus) {
        case EnumMatchStatusName.HALFTIME: //half time
          timeLabel = t('components.matchcard.halftime');
          break;
        case EnumMatchStatusName.FIRST_HALF: //first half
        case EnumMatchStatusName.SECOND_HALF: //second half
          // eslint-disable-next-line no-case-declarations
          const liveMatch = liveEvents.find((e) => e.gameNumber === match.eventId) ?? props.match;
          const timeText = liveMatch?.timeText ? Number(liveMatch.timeText?.split(':')[0]) : null;
          if (matchStatus == EnumMatchStatusName.FIRST_HALF && timeText >= 45) {
            const stoppageTime = timeText - 45;
            timeLabel = (
              <span>
                {matchStatus == EnumMatchStatusName.FIRST_HALF ? '45' : '90'}
                <sup>+{stoppageTime + 1}&apos;</sup>
              </span>
            );
            break;
          }
          if (matchStatus == EnumMatchStatusName.SECOND_HALF && timeText >= 90) {
            const stoppageTime = timeText - 90;
            timeLabel = (
              <span>
                {matchStatus == EnumMatchStatusName.FIRST_HALF ? '45' : '90'}
                <sup>+{stoppageTime + 1}&apos;</sup>
              </span>
            );
            break;
          }
          timeLabel = liveMatch?.timeText ? (
            <span>{Number(liveMatch.timeText?.split(':')[0]) + 1}&apos;</span>
          ) : null;
          break;
        case EnumMatchStatusName.PENALTIES: //Penalties
          timeLabel = t('components.matchcard.penalties');
          break;
        case EnumMatchStatusName.RETIRED: //retired
          timeLabel = t('components.matchcard.retired');
          break;
        case EnumMatchStatusName.AWAITING_EXTRA_TIME: //first break
          timeLabel = t('components.matchcard.break');
          break;
        default:
          timeLabel = '';
      }
    }
    return <span className="px-2 py-1 bg-dark text-white rounded-end-5 fs-8">{timeLabel}</span>;
  }

  function getMatchStatusMatchDateCard(match, sportType) {
    const { matchStatus } = match;
    const result = [];
    result.push(
      <span className="px-2 py-1 bg-success text-white fs-8" key={'matchStatus-live'}>
        {t('components.matchcard.live')}
      </span>
    );
    const gameDayandTime = (
      <>
        <span className="px-2 py-1 bg-primary text-white fs-8">
          {gameDateToMatchDay(props.startDate ? props.startDate : match.startDate)}
        </span>
        <span className="px-2 py-1 bg-dark text-white rounded-end-5 fs-8">
          {gameDateToMatchTime(props.startDate ? props.startDate : match.startDate)}
        </span>
      </>
    );
    if (matchStatus == EnumMatchStatusName.ENDED) {
      return (
        <>
          <span className="px-2 py-1 bg-primary text-white fs-8">
            {gameDateToMatchDay(props.startDate ? props.startDate : match.startDate)}
          </span>
          <span className="px-2 py-1 bg-dark text-white rounded-end-5 fs-8">
            {t('components.matchcard.end')}
          </span>
        </>
      );
    } else {
      if (sportType == sportTypeToSportId('Football')) {
        if (
          matchStatus > EnumMatchStatusName.NOT_STARTED &&
          matchStatus <= EnumMatchStatusName.SECOND_HALF
        ) {
          result.push(
            <span
              className="px-2 py-1 bg-dark text-white rounded-end-5 fs-8"
              key={'matchStatus-time'}>
              {getTime(match)}
            </span>
          );
          return <>{result}</>;
        } else if (matchStatus == EnumMatchStatusName.HALFTIME) {
          result.push(
            <span
              className="px-2 py-1 bg-dark text-white rounded-end-5 fs-8"
              key={'matchStatus-halftime'}>
              {t('components.matchcard.halftime')}
            </span>
          );
          return <>{result}</>;
        } else if (matchStatus == EnumMatchStatusName.PENALTIES) {
          result.push(
            <span
              className="px-2 py-1 bg-dark text-white rounded-end-5 fs-8"
              key={'matchStatus-penalties'}>
              {t('components.matchcard.penalties')}
            </span>
          );
          return <>{result}</>;
        } else {
          return gameDayandTime;
        }
      } else {
        if (matchStatus == EnumMatchStatusName.NOT_STARTED || matchStatus == -1) {
          return gameDayandTime;
        } else {
          return (
            <>
              <span className="px-2 py-1 text-white fs-8 bg-success">
                {t('components.matchcard.live')}
              </span>
              <span className="px-2 py-1 bg-dark text-white rounded-end-5 fs-8">
                {t(`components.matchcard.time.byMatchStatusId.${match.matchStatus}`)}
              </span>
            </>
          );
        }
      }
    }
  }

  const homeScores = [];
  const awayScores = [];
  const periodScoresLenght = match.periodScores?.length;
  match.periodScores?.forEach((element, index) => {
    if (index === periodScoresLenght - 1 && match.matchStatus != EnumMatchStatusName.ENDED) {
      homeScores.push(
        <span className="text-success" key={index}>
          {element.homeScore}{' '}
        </span>
      );
      awayScores.push(
        <span className="text-success" key={index}>
          {element.awayScore}{' '}
        </span>
      );
    } else {
      homeScores.push(<span key={index}>{element.homeScore} </span>);
      awayScores.push(<span key={index}>{element.awayScore} </span>);
    }
  });

  if (match.matchStatus == EnumMatchStatusName.ENDED) {
    homeScores.push(<span key={'homeScore'}> | {match.homeScore}</span>);
    awayScores.push(<span key={'awayScore'}> | {match.awayScore} </span>);
  }

  const getScore = (team, sportType, matchStatus) => {
    if (matchStatus != -1 && matchStatus != 0 && matchStatus != 100) {
      periodScoresLenght;
      if (
        sportType == sportTypeToSportId('Volleyball') ||
        sportType == sportTypeToSportId('Tennis') ||
        sportType == sportTypeToSportId('TableTennis')
      ) {
        return <span>{team == 'team1' ? homeScores : awayScores}</span>;
      }
    }
    return team == 'team1' ? match.homeScore : match.awayScore;
  };

  function getFirstHalfScore(team, sportType) {
    if (match.periodScores && match.periodScores.length >= 1) {
      let firstHalfScore;
      if (team == 'team1') {
        firstHalfScore =
          sportType == sportTypeToSportId('Football')
            ? homeScore1 + '|'
            : sportType == 1
              ? homeScore3
                ? firstHalfHomeScore + '|'
                : firstHalfHomeScore
              : '';
      } else {
        firstHalfScore =
          sportType == sportTypeToSportId('Football')
            ? awayScore1 + '|'
            : sportType == 1
              ? awayScore3
                ? firstHalfAwayScore + '|'
                : firstHalfAwayScore
              : '';
      }
      return <span>{firstHalfScore}</span>;
    } else {
      return null;
    }
  }

  const get = (team, sportType, match) => {
    const { matchStatus } = match;
    const array = [];
    if (match.matchStatus == EnumMatchStatusName.HALFTIME) {
      if (team == 'team1') {
        // array.push(
        //   <span className="position-absolute top-50 start-0 ms" key={'halftime'}>
        //     {t('components.matchcard.halftime')}
        //   </span>
        // );
      }
      if (
        sportType == sportTypeToSportId('Basketball') ||
        sportType == sportTypeToSportId('Football')
      ) {
        array.push(<span key={'firstHalfScore'}>{getFirstHalfScore(team, sportType)}</span>);
      }
    } else {
      array.push(
        <span key={'score'}>
          {getFirstHalfScore(team, sportType)} {getScore(team, sportType, matchStatus)}
        </span>
      );
    }
    return array;
  };

  const getTeams = (team, sportType, match) => {
    const teamName = team == 'team1' ? teams[0] : teams[1];
    const teamData = team == 'team1' ? match.home : match.away;
    const fallbackColor = team == 'team1' ? 'black' : 'white';

    return (
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-1 fs-7">
          <Jersey width={16} height={16} jersey={teamData?.jersey} color={fallbackColor} />
          <span>{teamName}</span>
        </div>
        <span className={`${isDesktop ? 'text-white' : 'text-muted'} `}>
          {get(team, sportType, match)}
        </span>
      </div>
    );
  };

  const isCustomBet = Array.isArray(props.customOutcomes) && props.customOutcomes.length > 0;
  let playerName = (match.outcomeName || '').split('&');
  const name = playerName[0].split(',')[1] ?? '';
  const surname = playerName[0].split(',')[0] ?? '';
  playerName = name + ' ' + surname;
  const marketId = (match.marketId || '').split('-')[0];

  // Check if match is live and can be bet on
  const isLive =
    match?.matchStatus > EnumMatchStatusName.NOT_STARTED &&
    match?.matchStatus !== EnumMatchStatusName.ENDED;

  // Check if match with status 0 (NOT_STARTED) has a valid start date (not in the past)
  const isNotStartedMatchValid =
    match?.matchStatus === EnumMatchStatusName.NOT_STARTED &&
    match?.startDate &&
    new Date(match.startDate) > new Date();

  const isClickable = isLive || isNotStartedMatchValid;

  const handleMatchCardClick = () => {
    if (isOutright && match?.eventId) {
      navigate(`/outrights/${encodeURIComponent(match.eventId)}`);
      return;
    }
    if (isClickable && match?.eventId) {
      navigate(`/match-detail#${match.eventId}`);
    }
  };

  // Outright bets carry an eventId like "sr:season:xxxx" and have empty
  // home/away/score blocks. Render a slim layout: outright tag + market name +
  // outcome + odds, skipping the irrelevant team/score rows.
  const isOutright = (match?.eventId || '').startsWith('sr:season:');
  const outrightEvent = useSelector((state) =>
    isOutright ? selectOutrightEventById(state, match.eventId) : null
  );
  if (isOutright) {
    const marketKey = `outrights.markets.${match.marketId}`;
    const translatedMarket = t(marketKey);
    const outrightMarket = outrightEvent?.markets?.find(
      (m) => m?.stringId === match.marketId
    );
    const marketLabel =
      translatedMarket && translatedMarket !== marketKey
        ? translatedMarket
        : outrightMarket?.name || match.marketId || '';
    const seasonKey = `outrights.events.${match.eventId}`;
    const translatedSeason = t(seasonKey);
    const seasonName =
      translatedSeason && translatedSeason !== seasonKey
        ? translatedSeason
        : outrightEvent?.se?.eventName ||
        outrightEvent?.se?.seasonInfo?.tournamentName ||
        match.eventId ||
        '';
    return (
      <div
        className={'px-2 py-1 rounded mb-1 border-gray-300'}
        onClick={handleMatchCardClick}
        style={{
          cursor: 'pointer',
          minHeight: 111,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
        <div className="d-flex align-items-center justify-content-between">
          <span
            className={isDesktop ? 'text-white' : 'text-muted'}
            style={{
              fontSize: '0.6rem',
              letterSpacing: '0.14em',
              fontWeight: 700,
              opacity: 0.7
            }}>
            {t('outrights.label').toUpperCase()}
          </span>
          <span style={{ opacity: 0.7 }} className={isDesktop ? 'icon-white' : ''}>
            {getSportTypeToIconForBetSlip(sportType) == null
              ? t(`sports.${sportType}`)
              : getSportTypeToIconForBetSlip(sportType)}
          </span>
        </div>
        <div
          className={`fw-bold ${isDesktop ? 'text-white' : 'text-dark'}`}
          style={{
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginTop: 2
          }}>
          {seasonName}
        </div>
        <div
          className={isDesktop ? 'text-white' : 'text-muted'}
          style={{
            fontSize: '0.7rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginTop: 2
          }}>
          {marketLabel}
        </div>
        <div className="d-flex align-items-center justify-content-between mt-1">
          <span
            className={`fw-bold ${isDesktop ? 'text-white' : 'text-dark'}`}
            style={{
              fontSize: '0.9rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0
            }}>
            {match.outcomeName}
          </span>
          <div className="d-flex align-items-center flex-shrink-0 ms-2">
            <span
              className={`me-2 fw-bolder ${isDesktop ? 'text-white' : 'text-dark'}`}
              style={{ fontSize: '0.9rem' }}>
              {Number(match.placeBetOdds).toFixed(2)}
            </span>
            {match.outcomeStatus === 0 && (
              <img width="14" height="14" src={`${AWS_BUCKET_URL}/Icons/lost.svg`} />
            )}
            {match.outcomeStatus === 1 && (
              <img width="14" height="14" src={`${AWS_BUCKET_URL}/Icons/right.svg`} />
            )}
            {match.outcomeStatus === 3 && (
              <img width="18" height="18" src={`${AWS_BUCKET_URL}/Icons/ban.svg`} />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={'px-2 py-1 rounded mb-1 cursor-pointer border-gray-300'}
      onClick={handleMatchCardClick}
      style={{ cursor: 'pointer' }}>
      <div className="d-flex justify-content-between mb-n3 ms-n2 lh-sm fs-7">
        <div className="d-flex text-center mb-3">
          {getMatchStatusMatchDateCard(match, sportType)}
        </div>
        {match.banker && (
          <div className="banker-bg " style={{ marginLeft: '30px', backgroundColor: '#3AA843' }}>
            <span className="text-white fw-bold fs-8 m-0 d-flex justify-content-center align-items-center text-center">
              B
            </span>
          </div>
        )}
        <div className="ms-auto">
          <div className={`pt-1 pb-2${isDesktop ? ' icon-white' : ''} `}>
            {getSportTypeToIconForBetSlip(sportType) == null
              ? t(`sports.${sportType}`)
              : getSportTypeToIconForBetSlip(sportType)}
            {/* - {t(`tournaments.${match.tournamentId}`)} */}
          </div>
        </div>
      </div>
      <Row>
        <div className={`pb-0 pt-1 fs-7 ${isDesktop ? 'text-white' : 'text-dark'}`}>
          {getTeams('team1', sportType, match)}
          {getTeams('team2', sportType, match)}
        </div>
      </Row>
      <div className="text-center my-1 fs-8 mt-n1">
        {/* <Col xs={3} className="bets_name"></Col> */}
        <div className={`m-auto ${isDesktop ? 'text-white' : 'text-dark'}`}>
          <small>
            Placed Score : {match.homeScoreAtPlaced} - {match.awayScoreAtPlaced}{' '}
          </small>
        </div>
      </div>
      {isCustomBet ? (
        <div className="px-2">
          <div className={`fs-8 ${isDesktop ? 'text-white' : 'text-muted'} mb-1`}>
            <span className="fw-bolder">
              {t('components.betmatchcard.customBet', 'Custom bet')}
            </span>
          </div>
          {props.customOutcomes.map((outcome, idx) => {
            const mktId = (outcome.marketId || '').split('-')[0];
            const pName = (outcome.outcomeName || '').split('&');
            const pn = (pName[0].split(',')[1] ?? '') + ' ' + (pName[0].split(',')[0] ?? '');
            return (
              <div key={idx} className="d-flex align-items-center py-1">
                <div className={`fs-8 ${isDesktop ? 'text-white' : 'text-muted'}`}>
                  {MarketIdToMarketName(mktId, outcome.marketId || '', false)}{' '}
                  <span className="fw-bolder">
                    {mktId === '888' ||
                      mktId === '891' ||
                      mktId === '40' ||
                      mktId === '38' ||
                      mktId === '39'
                      ? MyBetsTextCreator(
                        sportType,
                        mktId,
                        outcome.outcomeId,
                        outcome.outcomeName
                      ) +
                      ',' +
                      pn
                      : MyBetsTextCreator(sportType, mktId, outcome.outcomeId, outcome.outcomeName)}
                  </span>
                </div>
              </div>
            );
          })}
          <div className="d-flex align-items-center justify-content-between border-top pt-1 mt-1">
            <span className={`fs-8 fw-bolder ${isDesktop ? 'text-white' : 'text-muted'}`}>
              {t('components.betmatchcard.combinedOdds', 'Combined odds')}
            </span>
            <div className="d-flex align-items-center">
              <span className={`me-2 fw-bolder fs-8 ${isDesktop ? 'text-white' : 'text-muted'}`}>
                {Number(props.customCombinedOdds ?? 0).toFixed(2)}
              </span>
              {props.customOutcomeStatus === 0 && (
                <img width="15" height="15" src={`${AWS_BUCKET_URL}/Icons/lost.svg`} alt="lost" />
              )}
              {props.customOutcomeStatus === 1 && (
                <img width="15" height="15" src={`${AWS_BUCKET_URL}/Icons/right.svg`} alt="won" />
              )}
              {props.customOutcomeStatus === 3 && (
                <img width="20" height="20" src={`${AWS_BUCKET_URL}/Icons/ban.svg`} alt="void" />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="d-flex justify-content-between mb-n1">
            <div className={`fs-8 ${isDesktop ? 'text-white' : 'text-muted'}`}>
              {MarketIdToMarketName((match.marketId || '').split('-')[0], match.marketId, false)}{' '}
              <span className="fw-bolder">
                {marketId === EnumMarketName.ANYTIMEGOALSCORERWINNER_888.toString() ||
                  marketId === EnumMarketName.NEXTGOALSCORERWINNER_891.toString() ||
                  marketId === EnumMarketName.ANYTIMEGOALSCORER_40.toString() ||
                  marketId === EnumMarketName.NEXTGOALSCORER_38.toString() ||
                  marketId === EnumMarketName.LASTGOALSCORER_39.toString()
                  ? MyBetsTextCreator(sportType, match.marketId.split('-')[0], match.outcomeId) +
                  ',' +
                  playerName
                  : MyBetsTextCreator(
                    sportType,
                    match.marketId.split('-')[0],
                    match.outcomeId,
                    match?.outcomeName
                  )}
              </span>
            </div>
            <div className="d-flex align-items-center">
              <span className={`me-2 fw-bolder fs-8 ${isDesktop ? 'text-white' : 'text-muted'} `}>
                {match.placeBetOdds.toString()}
              </span>
              {match.outcomeStatus === 0 && (
                <img width="15" height="15" src={`${AWS_BUCKET_URL}/Icons/lost.svg`} />
              )}
              {match.outcomeStatus === 1 && (
                <img width="15" height="15" src={`${AWS_BUCKET_URL}/Icons/right.svg`} />
              )}
              {match.outcomeStatus === 3 && (
                <img width="20" height="20" src={`${AWS_BUCKET_URL}/Icons/ban.svg`} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default BetMatchCard;
